// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { X, FileText, Smartphone } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useFieldMode } from '@/hooks/useFieldMode';
import { getVisibleItems, CATEGORY_ORDER, CATEGORY_LABELS, type NavCategory } from './NavConfig';

interface HamburgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (viewKey: string) => void;
  currentView: string;
}

export const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  isOpen,
  onClose,
  onNavigate,
  currentView,
}) => {
  const { user } = useAuth();
  const userRole = user?.role || 'viewer';
  const visibleItems = getVisibleItems(userRole);
  const [fieldMode, setFieldMode] = useFieldMode(user?.role === 'canvasser');

  // Focus management
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const focusableItemsRef = useRef<HTMLButtonElement[]>([]);
  const previousActiveElementRef = useRef<Element | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Group items by category
  const itemsByCategory: Record<NavCategory, typeof visibleItems> = {
    geography: [],
    'field-ops': [],
    intelligence: [],
    planning: [],
    admin: [],
  };

  visibleItems.forEach(item => {
    itemsByCategory[item.category].push(item);
  });

  // Handle focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    // Save current focus and focus close button on menu open
    previousActiveElementRef.current = document.activeElement;
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
      setFocusedIndex(-1); // -1 indicates close button is focused
    }

    // Collect all focusable menu items
    const menuPanel = menuPanelRef.current;
    if (menuPanel) {
      // `[data-nav-item]`, NOT `button[role="menuitem"]`.
      //
      // The role was deliberately removed (see the comment on the buttons
      // below — role="menuitem" requires a role="menu" parent per WAI-ARIA,
      // and these are route links, not a menu widget). The selector here was
      // not updated with it, so this queried nothing, `focusableItemsRef` was
      // overwritten with an empty array on every open, and arrow-key
      // navigation through the menu silently did nothing.
      const focusableButtons = Array.from(
        menuPanel.querySelectorAll('button[data-nav-item]')
      ) as HTMLButtonElement[];
      focusableItemsRef.current = focusableButtons;
    }

    document.body.style.overflow = 'hidden';
  }, [isOpen]);

  // Handle Escape and arrow keys
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;

        case 'ArrowDown': {
          e.preventDefault();
          const items = focusableItemsRef.current;
          if (items.length === 0) return;

          let nextIndex = focusedIndex + 1;
          if (focusedIndex === -1) {
            // If close button is focused, move to first item
            nextIndex = 0;
          } else if (nextIndex >= items.length) {
            // Wrap to first item
            nextIndex = 0;
          }

          setFocusedIndex(nextIndex);
          items[nextIndex]?.focus();
          break;
        }

        case 'ArrowUp': {
          e.preventDefault();
          const items = focusableItemsRef.current;
          if (items.length === 0) return;

          let prevIndex = focusedIndex - 1;
          if (focusedIndex === -1) {
            // If close button is focused, move to last item
            prevIndex = items.length - 1;
          } else if (prevIndex < 0) {
            // Wrap to last item
            prevIndex = items.length - 1;
          }

          setFocusedIndex(prevIndex);
          items[prevIndex]?.focus();
          break;
        }

        case 'Home': {
          e.preventDefault();
          const items = focusableItemsRef.current;
          if (items.length === 0) return;
          setFocusedIndex(0);
          items[0]?.focus();
          break;
        }

        case 'End': {
          e.preventDefault();
          const items = focusableItemsRef.current;
          if (items.length === 0) return;
          const lastIndex = items.length - 1;
          setFocusedIndex(lastIndex);
          items[lastIndex]?.focus();
          break;
        }

        case 'Enter':
        case ' ':
          // Let default button behavior handle this
          break;

        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, focusedIndex]);

  const handleClose = () => {
    onClose();
    // Restore focus to previously focused element (hamburger button)
    if (previousActiveElementRef.current instanceof HTMLElement) {
      previousActiveElementRef.current.focus();
    }
    document.body.style.overflow = 'unset';
  };

  const handleItemClick = (viewKey: string) => {
    onNavigate(viewKey);
    handleClose();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm opacity-100 transition-opacity duration-200"
          onClick={handleClose}
          // Named so tests can assert on THE backdrop. They used to query
          // `[aria-hidden="true"]`, which also matches every decorative icon
          // in the tree and so was never really testing this element.
          data-menu-backdrop=""
          aria-hidden="true"
        />
      )}

      {/* Slide-out menu */}
      <div
        ref={menuPanelRef}
        className={`fixed top-0 left-0 h-dvh w-64 sm:w-72 bg-bg-raised border-r border-border-base z-50 overflow-y-auto transition-transform duration-200 ease-out will-change-transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ WebkitOverflowScrolling: 'touch' }}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        // The panel stays mounted and slides on a transform, so when it is
        // "closed" its buttons are still in the DOM — and were still
        // tab-reachable and still announced by screen readers. A keyboard user
        // could tab off the trigger straight into an off-screen menu.
        //
        // `inert` takes the whole subtree out of the tab order, out of the
        // accessibility tree, and out of hit-testing in one attribute, without
        // giving up the slide animation that unmounting would cost.
        {...(isOpen ? {} : { inert: '' as unknown as boolean })}
        aria-hidden={isOpen ? undefined : true}
      >
        {/* Menu header */}
        <div className="sticky top-0 bg-bg-raised border-b border-border-base px-4 py-4 flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-fg-secondary">Navigation</h2>
          <button
            ref={closeButtonRef}
            onClick={handleClose}
            className="p-2 rounded-md hover:bg-bg-elevated text-fg-muted hover:text-fg-primary transition-colors flex-shrink-0 inline-flex items-center justify-center min-h-11 min-w-11"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Menu content */}
        <div className="px-4 py-4 space-y-6">
          {CATEGORY_ORDER.map(category => {
            const items = itemsByCategory[category];
            if (items.length === 0) return null;

            const categoryId = `nav-category-${category}`;

            return (
              <section key={category} aria-labelledby={categoryId}>
                {/* Category header */}
                <h3
                  id={categoryId}
                  className="text-xs font-bold uppercase tracking-wider text-fg-muted mb-2"
                >
                  {CATEGORY_LABELS[category]}
                </h3>

                {/* Category items — navigation buttons, not menuitems.
                    role="menuitem" would require a role="menu" parent per
                    WAI-ARIA; these are route links, not a menu widget. */}
                <div className="space-y-1">
                  {items.map((item, itemIndex) => {
                    const isActive = currentView === item.key;
                    const globalIndex = itemIndex; // Simple index; real implementation may vary

                    return (
                      <button
                        key={item.key}
                        ref={(el) => {
                          if (el) focusableItemsRef.current[globalIndex] = el;
                        }}
                        onClick={() => handleItemClick(item.key)}
                        title={item.description}
                        // The hook the focus trap keys off. Every nav button
                        // carries it; the close button deliberately does not.
                        data-nav-item=""
                        aria-current={isActive ? 'page' : undefined}
                        className={`w-full text-left flex items-center gap-3 py-3 px-4 rounded-md transition-colors min-h-11 ${
                          isActive
                            ? 'border-l-2 border-accent bg-bg-elevated/60 text-fg-primary'
                            : 'text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary'
                        }`}
                      >
                        <span
                          className={`flex-shrink-0 transition-colors ${
                            isActive ? 'text-accent-fg' : ''
                          }`}
                        >
                          <item.Icon className="w-5 h-5" aria-hidden="true" />
                        </span>
                        <span
                          className={`text-sm ${
                            isActive ? 'font-semibold' : 'font-medium'
                          }`}
                        >
                          {item.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {/* Footer link */}
        <div className="sticky bottom-0 border-t border-border-base bg-bg-raised px-4 py-4 space-y-2">
          <button
            type="button"
            onClick={() => setFieldMode(!fieldMode)}
            className="w-full inline-flex items-center justify-between gap-2 px-4 py-3 rounded-md hover:bg-bg-elevated transition-colors min-h-11"
            aria-pressed={fieldMode}
          >
            <span className="inline-flex items-center gap-2 text-sm text-fg-secondary">
              <Smartphone className="w-4 h-4" aria-hidden="true" />
              Field Mode
            </span>
            <span
              className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-colors ${
                fieldMode ? 'bg-accent' : 'bg-border-subtle'
              }`}
              aria-hidden="true"
            >
              <span
                className={`w-5 h-5 bg-fg-primary rounded-full transform transition-transform ${
                  fieldMode ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </span>
          </button>
          <a
            href="/maps/42x42Whole.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-fg-muted hover:text-fg-secondary transition-colors min-h-11 py-2"
          >
            <FileText className="w-4 h-4" aria-hidden="true" />
            <span>County Map (PDF)</span>
          </a>
        </div>
      </div>
    </>
  );
};
